import { useCallback } from 'react';
import {
  patchFile, deleteFile, copyFile, type NodePublic,
} from '../../api/workspace';
import { requestPresignedDownload } from '../../api/files';
import { layerAlert, layerConfirm, layerPrompt } from '../../components/LayerPopup/LayerPopup';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 행 메뉴·선택 툴바가 실제로 수행하는 동작들 (2026-08-14).
 *
 * WorkspacePage 에 그대로 두면 렌더 함수가 700줄을 넘는다. 화면과 무관한 "무엇을 하는가" 만
 * 여기로 뺀다 — 모달을 여는 동작(공유·이동·색상)은 상태가 필요하므로 페이지에 남기고,
 * **되묻고 실행하고 갱신하는** 것들만 담는다.
 *
 * 여러 개를 한 번에 처리할 때는 **하나가 실패해도 나머지를 계속한다.** 전부 중단하면
 * "10개 중 3번째가 남의 파일이라" 나머지 7개가 아무 이유 없이 안 된다.
 */

interface Options {
  /** 처리 후 목록 갱신. */
  refresh: () => Promise<void> | void;
  /** 처리 후 선택 해제. */
  clearSelection: () => void;
}

function reasonOf(err: unknown): string {
  return (err as any)?.response?.data?.message ?? (err as Error)?.message ?? '';
}

export function useWorkspaceActions({ refresh, clearSelection }: Options) {
  /** 여러 건을 순차 처리하고 성공/실패 수를 알린다. */
  const runBatch = useCallback(async (
    nodes: readonly NodePublic[],
    fn: (node: NodePublic) => Promise<unknown>,
  ) => {
    let ok = 0;
    const failed: string[] = [];
    for (const n of nodes) {
      try {
        await fn(n);
        ok += 1;
      } catch (err) {
        failed.push(`${n.name}: ${reasonOf(err)}`);
      }
    }
    await refresh();
    clearSelection();
    if (failed.length > 0) {
      // 실패 사유를 전부 보여 준다 — "몇 개 실패" 만으로는 무엇을 고쳐야 할지 모른다.
      void layerAlert(
        `${tWorkspace('workspace.bulk.partial', { ok, fail: failed.length })}\n\n${failed.join('\n')}`,
      );
    }
    return { ok, failed: failed.length };
  }, [refresh, clearSelection]);

  /** 다운로드 — 여러 건이면 순차로 앵커를 만든다. */
  const download = useCallback(async (nodes: readonly NodePublic[]) => {
    for (const n of nodes) {
      if (n.type === 'folder') continue;   // 폴더는 받을 것이 없다
      try {
        const res = await requestPresignedDownload(n.id);
        // ⚠ window.open 금지(설치형에서 팝업 차단·별창 분리) — 앵커 다운로드로 처리한다.
        const a = document.createElement('a');
        a.href = res.downloadUrl;
        a.download = n.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        void layerAlert(`${tWorkspace('workspace.editor.download_failed')}: ${reasonOf(err)}`);
        return;
      }
    }
  }, []);

  const copyLink = useCallback(async (node: NodePublic) => {
    const url = `${window.location.origin}/workspace?file=${node.id}`;
    try {
      await navigator.clipboard.writeText(url);
      void layerAlert(tWorkspace('workspace.row.link_copied'));
    } catch {
      // 클립보드 권한이 없을 수 있다 — 링크를 그대로 띄워 직접 복사하게 한다.
      void layerAlert(url);
    }
  }, []);

  const rename = useCallback(async (node: NodePublic) => {
    const next = await layerPrompt(tWorkspace('workspace.row.rename_prompt'), {
      defaultValue: node.name,
    });
    if (next === null || next.trim() === '' || next === node.name) return;
    try {
      await patchFile(node.id, { name: next.trim() });
      await refresh();
    } catch (err) {
      void layerAlert(reasonOf(err));
    }
  }, [refresh]);

  /** 확인 없이 바로 지운다 — 사용처 경고 모달이 이미 확인을 받은 뒤에 쓴다. */
  const removeConfirmed = useCallback(async (nodes: readonly NodePublic[]) => {
    await runBatch(nodes, (n) => deleteFile(n.id));
  }, [runBatch]);

  const remove = useCallback(async (nodes: readonly NodePublic[]) => {
    const label = nodes.length === 1
      ? tWorkspace('workspace.row.confirm_delete', { name: nodes[0]!.name })
      : tWorkspace('workspace.row.confirm_delete', { name: `${nodes[0]!.name} 외 ${nodes.length - 1}개` });
    if (!(await layerConfirm(label))) return;
    await removeConfirmed(nodes);
  }, [removeConfirmed]);

  /** 복제 — 같은 폴더에 "(사본)" 이름으로. 이름은 서버가 정한다(중복 회피). */
  const duplicate = useCallback(async (nodes: readonly NodePublic[]) => {
    await runBatch(nodes, (n) => copyFile(n.id));
  }, [runBatch]);

  const moveTo = useCallback(async (nodes: readonly NodePublic[], parentId: string | null) => {
    await runBatch(nodes, (n) => patchFile(n.id, { parentId }));
  }, [runBatch]);

  const copyTo = useCallback(async (nodes: readonly NodePublic[], parentId: string | null) => {
    await runBatch(nodes, (n) => copyFile(n.id, { parentId }));
  }, [runBatch]);

  const setColor = useCallback(async (node: NodePublic, color: string | null) => {
    await patchFile(node.id, { color });
    await refresh();
  }, [refresh]);

  return {
    download, copyLink, rename, remove, removeConfirmed, duplicate, moveTo, copyTo, setColor,
    // 2026-08-14 additive — 페이지가 자기 동작(회사공유 등록/해제)에도 같은 일괄 처리
    // 규칙(하나 실패해도 계속 · 끝나고 사유를 모아 알림 · 갱신 후 선택 해제)을 쓰게 한다.
    runBatch,
  };
}
